import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
// Categories come from /api/complaint-categories, which carries no permission.
// Everything else on this screen runs on /api/member/complaints*, so a
// resident role needs only member_portal.complaints — no complaint.* code.
import { getComplaintCategories } from '../../../redux/actions/SocietyPortal/ComplaintAction/ComplaintAction';
import {
    getMemberFlats,
    getMemberComplaintEnums,
    createMemberComplaint,
    uploadMemberComplaintAttachments,
} from '../../../redux/actions/SocietyPortal/MemberPortalAction/MemberPortalAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_MY_COMPLAINTS,
    SOCIETY_PORTAL_MY_COMPLAINTS_VIEW,
} from '../../../Routing/routes';

const PRIORITY_HINT = {
    LOW:    { label: 'Low',    desc: 'Cosmetic or non-urgent',          color: 'tw-border-slate-200 hover:tw-border-slate-300' },
    MEDIUM: { label: 'Medium', desc: 'Standard turnaround',              color: 'tw-border-blue-200 hover:tw-border-blue-300' },
    HIGH:   { label: 'High',   desc: 'Needs quick attention',            color: 'tw-border-amber-200 hover:tw-border-amber-300' },
    URGENT: { label: 'Urgent', desc: 'Safety or blocking issue',          color: 'tw-border-rose-200 hover:tw-border-rose-300' },
};

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const RaiseComplaint = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { categoriesData } = useSelector(state => state.ComplaintReducer || {});
    // The member's own flats, so the complaint is tied to their unit without
    // them having to pick it. Owners of several flats get a picker instead.
    const {
        flatsData,
        flatsLoading,
        complaintEnumsData,
        createComplaintLoading,
    } = useSelector(state => state.MemberPortalReducer || {});
    const myFlats = useMemo(() => (Array.isArray(flatsData) ? flatsData : []), [flatsData]);
    const { can } = usePermissions();
    const hasPortal = can(PERMISSIONS.MEMBER_PORTAL_COMPLAINTS);

    const [form, setForm] = useState({
        category_id: '',
        subject: '',
        description: '',
        priority: 'MEDIUM',
        flat_id: '',
        // 'flat' — about the member's own home; 'wing' — about a wing's common
        // area (a lift, a corridor light), sent as wing_id with no flat.
        scope: 'flat',
        wing_id: '',
    });
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (societyId && !complaintEnumsData) dispatch(getMemberComplaintEnums({ societyId }));
    }, [dispatch, societyId, complaintEnumsData]);
    useEffect(() => {
        if (societyId) dispatch(getComplaintCategories({ societyId }));
    }, [dispatch, societyId]);
    useEffect(() => {
        // /api/member/* is gated on member_portal.*; the route is too, so
        // this is belt-and-braces against a stale permission list.
        if (societyId && hasPortal) dispatch(getMemberFlats({ societyId }));
    }, [dispatch, societyId, hasPortal]);
    useEffect(() => {
        // Auto-select the member's flat once it loads; keep any pick they made.
        if (myFlats.length === 0) return;
        setForm(f => (f.flat_id ? f : { ...f, flat_id: myFlats[0].flat_id }));
    }, [myFlats]);

    const categories = Array.isArray(categoriesData) ? categoriesData : [];
    // The society's wings ride along on the member complaint enums.
    const wings = Array.isArray(complaintEnumsData?.wings) ? complaintEnumsData.wings : [];
    const wingLabel = (w) => `Wing ${w?.title || w?.number || w?.wing_id}`;
    // A member with no flat can only raise against a wing (or nothing).
    const scope = myFlats.length === 0 && !flatsLoading ? 'wing' : form.scope;

    const setScope = (next) => {
        setForm(f => {
            if (next !== 'wing' || f.wing_id) return { ...f, scope: next };
            // Start the wing picker on the wing of the flat they had selected.
            const flat = myFlats.find(x => String(x.flat_id) === String(f.flat_id)) || myFlats[0];
            const wingId = flat?.wing?.wing_id ?? flat?.wing_id;
            return { ...f, scope: next, wing_id: wingId ? String(wingId) : '' };
        });
        setErrors(e => ({ ...e, wing_id: undefined }));
    };

    const flatLabel = (f) => {
        const wing = f?.wing?.title || f?.wing?.number;
        return [wing ? `Wing ${wing}` : null, `Flat ${f?.flat_number ?? f?.flat_id}`]
            .filter(Boolean)
            .join(' · ');
    };
    const slaHours = complaintEnumsData?.slaHours || {};

    const slaText = useMemo(() => {
        const h = slaHours[form.priority];
        if (!h) return null;
        if (h < 48) return `~${h}h target resolution`;
        return `~${Math.round(h / 24)} day target resolution`;
    }, [slaHours, form.priority]);

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        setErrors(e => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.category_id) e.category_id = 'Please pick a category';
        if (!form.subject.trim()) e.subject = 'Give it a short title';
        else if (form.subject.trim().length < 3) e.subject = 'Minimum 3 characters';
        if (!form.description.trim()) e.description = 'Describe what happened';
        else if (form.description.trim().length < 5) e.description = 'Minimum 5 characters';
        if (scope === 'wing' && myFlats.length > 0 && !form.wing_id) e.wing_id = 'Pick the wing this is about';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleFilesChange = (e) => {
        const picked = Array.from(e.target.files || []);
        setFiles(prev => {
            // Append instead of replace — users often click the picker once
            // per file rather than multi-selecting in one shot. Dedupe by
            // (name + size) so re-picking the same file doesn't add twice,
            // and cap the total at 5 to match the BE + label.
            const key = (f) => `${f.name}::${f.size}`;
            const seen = new Set(prev.map(key));
            const next = [...prev];
            for (const f of picked) {
                if (next.length >= 5) break;
                if (!seen.has(key(f))) {
                    next.push(f);
                    seen.add(key(f));
                }
            }
            return next;
        });
        // Reset the input so the same file can be picked again after removal
        // (browsers suppress onChange when the value is unchanged).
        e.target.value = '';
    };
    const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

    const handleSubmit = (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        // society_id and the raiser come from the token on the member endpoint;
        // the body carries only what was chosen here.
        dispatch(createMemberComplaint({
            societyId,
            category_id: Number(form.category_id),
            subject: form.subject.trim(),
            description: form.description.trim(),
            priority: form.priority,
            // Sent explicitly so the ticket shows the same flat the member saw
            // on the form. The backend falls back to their primary flat anyway.
            // A wing complaint sends only the wing, so no flat is attached.
            ...(scope === 'wing'
                ? { wing_id: form.wing_id ? Number(form.wing_id) : undefined }
                : { flat_id: form.flat_id ? Number(form.flat_id) : undefined }),
            onSuccess: (res) => {
                const complaintId = res?.data?.complaint_id;
                if (complaintId && files.length > 0) {
                    // Upload attachments in the background — the user is already
                    // redirected to the detail page, where the attachments render.
                    dispatch(uploadMemberComplaintAttachments({ id: complaintId, files }));
                }
                if (complaintId) {
                    navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_COMPLAINTS_VIEW.replace(':id', complaintId));
                } else {
                    navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_COMPLAINTS);
                }
            },
        }));
    };

    if (categories.length === 0 && !complaintEnumsData) {
        return (
            <div className="tw-p-10 tw-flex tw-justify-center">
                <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-gray-200 tw-border-t-rose-500" />
            </div>
        );
    }

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-max-w-3xl tw-mx-auto tw-space-y-4">
                <button
                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_COMPLAINTS)}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800"
                >
                    <Icon icon="mdi:arrow-left" /> Back to My Complaints
                </button>

                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                    <div className="tw-p-4 tw-border-b tw-border-gray-100">
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Raise a Complaint</h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            The society team will pick this up and keep you updated. Add photos so they can spot the issue quickly.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="tw-p-5 tw-space-y-5">
                        {/* Category */}
                        <div>
                            <label className="tw-block tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-2">
                                What's this about? <span className="tw-text-rose-500">*</span>
                            </label>
                            {categories.length === 0 ? (
                                <div className="tw-p-3 tw-bg-amber-50 tw-border tw-border-amber-100 tw-rounded-md tw-text-[11px] tw-text-amber-800">
                                    Your society hasn't set up complaint categories yet. Please ask an admin to add them.
                                </div>
                            ) : (
                                <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-2">
                                    {categories.map(cat => {
                                        const active = String(form.category_id) === String(cat.category_id);
                                        return (
                                            <button
                                                type="button"
                                                key={cat.category_id}
                                                onClick={() => set('category_id', cat.category_id)}
                                                className={`tw-p-3 tw-rounded-lg tw-border-2 tw-text-left tw-transition-all ${active ? 'tw-border-rose-500 tw-bg-rose-50' : 'tw-border-gray-100 hover:tw-border-gray-200 tw-bg-white'}`}
                                            >
                                                <div className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-mb-1.5 ${active ? 'tw-bg-rose-100 tw-text-rose-700' : 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                    <Icon icon={cat.icon || 'mdi:tag-outline'} className="tw-text-base" />
                                                </div>
                                                <p className="tw-text-xs tw-font-semibold tw-text-gray-800 tw-truncate">{cat.name}</p>
                                                {cat.description && <p className="tw-text-[10px] tw-text-gray-500 tw-truncate">{cat.description}</p>}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            {errors.category_id && <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.category_id}</p>}
                        </div>

                        {/* What the complaint is about — their flat or a wing's common area */}
                        {wings.length > 0 && myFlats.length > 0 && (
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-1">
                                    This complaint is about
                                </label>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                                    {[
                                        { value: 'flat', label: 'My flat', icon: 'mdi:home-account' },
                                        { value: 'wing', label: 'A wing (common area)', icon: 'mdi:office-building-outline' },
                                    ].map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setScope(opt.value)}
                                            className={`tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-border tw-text-xs tw-font-medium tw-transition-colors ${
                                                scope === opt.value
                                                    ? 'tw-border-rose-500 tw-bg-rose-50 tw-text-rose-700'
                                                    : 'tw-border-gray-200 tw-text-gray-600 hover:tw-border-gray-300'
                                            }`}
                                        >
                                            <Icon icon={opt.icon} className="tw-text-base" />
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Wing — for lifts, corridors and other shared areas of a wing */}
                        {scope === 'wing' && wings.length > 0 && (
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-1">
                                    Wing {myFlats.length > 0 && <span className="tw-text-rose-500">*</span>}
                                </label>
                                <select
                                    value={form.wing_id || ''}
                                    onChange={e => set('wing_id', e.target.value)}
                                    className={`tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 ${errors.wing_id ? 'tw-border-rose-400' : 'tw-border-gray-200'}`}
                                >
                                    <option value="">Select wing</option>
                                    {wings.map(w => (
                                        <option key={w.wing_id} value={w.wing_id}>{wingLabel(w)}</option>
                                    ))}
                                </select>
                                {errors.wing_id
                                    ? <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.wing_id}</p>
                                    : <p className="tw-text-[10px] tw-text-gray-500 tw-mt-1">For shared areas of the wing, such as a lift, staircase or corridor light. No flat is attached.</p>}
                            </div>
                        )}

                        {/* Flat — auto-selected from the member's own flats */}
                        {scope === 'flat' && (flatsLoading || myFlats.length > 0) && (
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-1">
                                    Your flat
                                </label>
                                {myFlats.length === 0 ? (
                                    <p className="tw-text-[11px] tw-text-gray-400">Loading your flat…</p>
                                ) : myFlats.length === 1 ? (
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-md tw-text-xs tw-text-gray-800">
                                        <Icon icon="mdi:home-account" className="tw-text-rose-500 tw-text-base" />
                                        <span className="tw-font-medium">{flatLabel(myFlats[0])}</span>
                                    </div>
                                ) : (
                                    <select
                                        value={form.flat_id || ''}
                                        onChange={e => set('flat_id', e.target.value)}
                                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                                    >
                                        {myFlats.map(f => (
                                            <option key={f.flat_id} value={f.flat_id}>{flatLabel(f)}</option>
                                        ))}
                                    </select>
                                )}
                                {myFlats.length > 0 && (
                                    <p className="tw-text-[10px] tw-text-gray-500 tw-mt-1">
                                        {myFlats.length > 1
                                            ? 'Pick which of your flats this complaint is about.'
                                            : 'This complaint will be logged against your flat.'}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Subject */}
                        <div>
                            <label className="tw-block tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-1">
                                Title <span className="tw-text-rose-500">*</span>
                            </label>
                            <input
                                value={form.subject}
                                onChange={e => set('subject', e.target.value)}
                                placeholder="e.g. Water leak in kitchen"
                                className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                            />
                            {errors.subject && <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.subject}</p>}
                        </div>

                        {/* Description */}
                        <div>
                            <label className="tw-block tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-1">
                                Describe the issue <span className="tw-text-rose-500">*</span>
                            </label>
                            <textarea
                                rows={4}
                                value={form.description}
                                onChange={e => set('description', e.target.value)}
                                placeholder="When did it start? What's affected? Any special access instructions?"
                                className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                            />
                            {errors.description && <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.description}</p>}
                        </div>

                        {/* Priority */}
                        <div>
                            <label className="tw-block tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-2">
                                How urgent is it?
                            </label>
                            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-2">
                                {['LOW', 'MEDIUM', 'HIGH', 'URGENT'].map(p => {
                                    const active = form.priority === p;
                                    const hint = PRIORITY_HINT[p];
                                    return (
                                        <button
                                            type="button"
                                            key={p}
                                            onClick={() => set('priority', p)}
                                            className={`tw-p-3 tw-rounded-lg tw-border-2 tw-text-left tw-transition-all ${active ? 'tw-border-rose-500 tw-bg-rose-50' : `tw-bg-white ${hint.color}`}`}
                                        >
                                            <p className="tw-text-xs tw-font-semibold tw-text-gray-800">{hint.label}</p>
                                            <p className="tw-text-[10px] tw-text-gray-500 tw-mt-0.5">{hint.desc}</p>
                                        </button>
                                    );
                                })}
                            </div>
                            {slaText && (
                                <p className="tw-text-[11px] tw-text-gray-500 tw-mt-2 tw-flex tw-items-center tw-gap-1">
                                    <Icon icon="mdi:clock-outline" className="tw-text-base" /> {humanize(form.priority)} priority: {slaText}
                                </p>
                            )}
                        </div>

                        {/* Attachments */}
                        <div>
                            <label className="tw-block tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-1">
                                Photos or documents <span className="tw-text-gray-400 tw-font-normal">(optional)</span>
                            </label>
                            <label className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-6 tw-border-2 tw-border-dashed tw-border-gray-200 tw-rounded-lg tw-text-xs tw-text-gray-500 tw-cursor-pointer hover:tw-bg-gray-50 hover:tw-border-gray-300 tw-transition-colors">
                                <Icon icon="mdi:cloud-upload-outline" className="tw-text-lg" />
                                Click to attach up to 5 files (10MB each)
                                <input type="file" multiple className="tw-hidden" onChange={handleFilesChange} />
                            </label>
                            {files.length > 0 && (
                                <div className="tw-mt-2 tw-space-y-1.5">
                                    {files.map((f, i) => (
                                        <div key={i} className="tw-flex tw-items-center tw-gap-2 tw-px-2.5 tw-py-1.5 tw-bg-gray-50 tw-rounded-md tw-text-[11px]">
                                            <Icon icon="mdi:paperclip" className="tw-text-gray-400" />
                                            <span className="tw-flex-1 tw-truncate tw-text-gray-700">{f.name}</span>
                                            <span className="tw-text-gray-400">{Math.round(f.size / 1024)} KB</span>
                                            <button type="button" onClick={() => removeFile(i)} className="tw-p-0.5 tw-rounded hover:tw-bg-gray-200 tw-text-gray-400">
                                                <Icon icon="mdi:close" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2 tw-border-t tw-border-gray-100">
                            <button
                                type="button"
                                onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_COMPLAINTS)}
                                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={createComplaintLoading || categories.length === 0}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            >
                                {createComplaintLoading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                                <Icon icon="mdi:send" /> Submit Complaint
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RaiseComplaint;
